import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Plus, X, Cpu, Check, Eye, Code, Zap, Shield, Mail, Globe, Lock, BarChart3, Cloud, Users, Layers } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DashboardLayout } from "@/pages/DashboardPage";
import { useAuth, API } from "@/App";
import axios from "axios";
import { RuntimeConfigurator } from '@/components/runtime/RuntimeConfigurator';

const CATEGORIES = [
  { id: "email_security", label: "Email Security", icon: Mail, desc: "Phishing detection, email analysis, spam filtering" },
  { id: "vulnerability", label: "Vulnerability Scanning", icon: Shield, desc: "Web app testing, OWASP, CVE scanning" },
  { id: "network", label: "Network Security", icon: Globe, desc: "Traffic analysis, DDoS, firewall rules" },
  { id: "crypto_security", label: "Crypto & Blockchain", icon: Lock, desc: "Wallet monitoring, chain analysis, fraud" },
  { id: "compliance", label: "Compliance & Audit", icon: BarChart3, desc: "SOC2, ISO 27001, GDPR, HIPAA" },
  { id: "siem", label: "SIEM & Log Analysis", icon: Layers, desc: "Log parsing, anomaly detection, alerting" },
  { id: "identity", label: "Identity & Access", icon: Users, desc: "IAM, credential stuffing, session mgmt" },
  { id: "cloud_security", label: "Cloud Security", icon: Cloud, desc: "AWS/GCP/Azure posture, misconfig scanning" },
];

const PRICING_OPTIONS = [
  { id: "free", label: "Free", desc: "Open source, community-driven", color: "emerald" },
  { id: "freemium", label: "Freemium", desc: "Free tier with paid premium features", color: "blue" },
  { id: "paid", label: "Paid", desc: "Monthly subscription for full access", color: "amber" },
];

const STEPS = ["Basics", "Category", "Capabilities", "Pricing", "Review"];

export default function AgentBuilderPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  // Form state
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [capabilities, setCapabilities] = useState([]);
  const [capInput, setCapInput] = useState("");
  const [pricingType, setPricingType] = useState("free");
  const [price, setPrice] = useState("");
  const [engine, setEngine] = useState('prompt_guard');
  const [terms, setTerms] = useState('');
  const [publishedId, setPublishedId] = useState('');

  const addCapability = () => {
    const trimmed = capInput.trim();
    if (trimmed && !capabilities.includes(trimmed) && capabilities.length < 8) {
      setCapabilities([...capabilities, trimmed]);
      setCapInput("");
    }
  };

  const removeCapability = (cap) => {
    setCapabilities(capabilities.filter((c) => c !== cap));
  };

  const canProceed = () => {
    switch (step) {
      case 0: return name.trim().length >= 3 && description.trim().length >= 20;
      case 1: return !!category;
      case 2: return capabilities.length >= 2 && terms.split('\n').filter(t => t.trim()).length <= 20 && terms.split('\n').every(t => t.trim().length <= 80);
      case 3: return pricingType && (pricingType === "free" || (price && parseFloat(price) > 0));
      case 4: return true;
      default: return false;
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const headers = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;
      const response = await axios.post(`${API}/marketplace/agents`, {
        name: name.trim(),
        description: description.trim(),
        category,
        capabilities,
        pricing_type: pricingType,
        price: pricingType !== "free" ? parseFloat(price) : null,
        is_community: true,
        runtime_config: { engine_id: engine, blocked_terms: terms.split('\n').map(t => t.trim()).filter(Boolean) },
      }, { headers, withCredentials: true });
      setPublishedId(response.data.agent_id);
      setSubmitted(true);
    } catch (err) {
      setError(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : "Failed to publish agent. Check your configuration and retry.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <DashboardLayout title="Agent Builder">
        <div className="max-w-xl mx-auto text-center py-20" data-testid="agent-submitted">
          <div className="w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto mb-6">
            <Check className="w-8 h-8 text-emerald-400" strokeWidth={2} />
          </div>
          <h2 className="text-2xl font-bold tracking-tight mb-3" style={{ fontFamily: "var(--font-heading)" }}>Executable Agent Published</h2>
          <p className="text-sm text-zinc-400 mb-2 max-w-md mx-auto">
            <span className="text-zinc-200 font-semibold">{name}</span> is available in the marketplace with your selected scan engine and policy terms.
          </p>
          <p className="text-xs text-zinc-500 mb-8">Free runtime beta. No billing, payout, or email notification has been triggered.</p>
          <Button data-testid="builder-run-published" className="mb-4 bg-cyan-600" onClick={() => navigate(`/runtime?agent=${publishedId}`)}>Run your agent</Button>
          <div className="flex gap-3 justify-center">
            <Button onClick={() => navigate("/marketplace")} className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm font-semibold" data-testid="back-to-marketplace-btn">
              Back to Marketplace
            </Button>
            <Button onClick={() => { setSubmitted(false); setStep(0); setName(""); setDescription(""); setCategory(""); setCapabilities([]); setPricingType("free"); setPrice(""); }} variant="outline" className="border-zinc-800 text-zinc-400 hover:bg-zinc-900 hover:text-zinc-50 rounded-sm bg-transparent" data-testid="build-another-btn">
              Build Another Agent
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const selectedCat = CATEGORIES.find((c) => c.id === category);
  const CatIcon = selectedCat?.icon || Cpu;

  return (
    <DashboardLayout title="Agent Builder">
      <div className="max-w-3xl mx-auto" data-testid="agent-builder-page">
        {/* Progress Steps */}
        <div className="flex items-center justify-between mb-8">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center">
              <div className={`flex items-center gap-2 ${i <= step ? "opacity-100" : "opacity-40"}`}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                  i < step ? "bg-emerald-500 text-white" : i === step ? "bg-blue-600 text-white" : "bg-zinc-800 text-zinc-500"
                }`}>{i < step ? <Check className="w-3.5 h-3.5" /> : i + 1}</div>
                <span className="text-xs text-zinc-400 font-semibold hidden sm:inline">{s}</span>
              </div>
              {i < STEPS.length - 1 && <div className={`w-8 sm:w-16 h-px mx-2 ${i < step ? "bg-emerald-500" : "bg-zinc-800"}`} />}
            </div>
          ))}
        </div>

        {/* Step Content */}
        <div className="trace-border rounded-sm bg-zinc-900 p-8">

          {/* Step 0: Basics */}
          {step === 0 && (
            <div className="space-y-6" data-testid="step-basics">
              <div>
                <h3 className="text-lg font-bold tracking-tight mb-1" style={{ fontFamily: "var(--font-heading)" }}>Agent Details</h3>
                <p className="text-xs text-zinc-500">Give your security agent a name and description.</p>
              </div>
              <div>
                <Label className="text-xs tracking-[0.15em] uppercase text-zinc-400 font-bold">Agent Name</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., FirewallGuard, TokenAnalyzer, SSLChecker..."
                  className="mt-2 bg-zinc-950 border-zinc-800 focus:ring-1 focus:ring-blue-500 rounded-sm h-11 text-zinc-50 placeholder:text-zinc-600"
                  maxLength={40}
                  data-testid="agent-name-input"
                />
                <p className="text-[10px] text-zinc-600 mt-1">{name.length}/40 characters</p>
              </div>
              <div>
                <Label className="text-xs tracking-[0.15em] uppercase text-zinc-400 font-bold">Description</Label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe what your agent does, what security problems it solves, and how it works..."
                  className="mt-2 bg-zinc-950 border-zinc-800 focus:ring-1 focus:ring-blue-500 rounded-sm text-zinc-50 placeholder:text-zinc-600 min-h-[120px]"
                  maxLength={500}
                  data-testid="agent-desc-input"
                />
                <p className="text-[10px] text-zinc-600 mt-1">{description.length}/500 characters (min 20)</p>
              </div>
            </div>
          )}

          {/* Step 1: Category */}
          {step === 1 && (
            <div className="space-y-6" data-testid="step-category">
              <div>
                <h3 className="text-lg font-bold tracking-tight mb-1" style={{ fontFamily: "var(--font-heading)" }}>Choose a Category</h3>
                <p className="text-xs text-zinc-500">Select the primary security domain for your agent.</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {CATEGORIES.map((cat) => {
                  const active = category === cat.id;
                  return (
                    <button
                      key={cat.id}
                      onClick={() => setCategory(cat.id)}
                      className={`text-left p-4 rounded-sm border transition-all ${
                        active ? "bg-blue-600/10 border-blue-600/30 ring-1 ring-blue-600/20" : "bg-zinc-800/50 border-zinc-800 hover:border-zinc-700"
                      }`}
                      data-testid={`cat-select-${cat.id}`}
                    >
                      <div className="flex items-center gap-3 mb-1">
                        <cat.icon className={`w-4 h-4 ${active ? "text-blue-400" : "text-zinc-500"}`} strokeWidth={1.5} />
                        <span className={`text-sm font-semibold ${active ? "text-blue-400" : "text-zinc-300"}`}>{cat.label}</span>
                      </div>
                      <p className="text-xs text-zinc-500 ml-7">{cat.desc}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 2: Capabilities */}
          {step === 2 && (
            <div className="space-y-6" data-testid="step-capabilities">
              <div>
                <h3 className="text-lg font-bold tracking-tight mb-1" style={{ fontFamily: "var(--font-heading)" }}>Agent Capabilities</h3>
                <p className="text-xs text-zinc-500">Add 2-8 capabilities your agent provides. These appear as tags on the marketplace listing.</p>
              </div>
              <div className="flex gap-2">
                <Input
                  value={capInput}
                  onChange={(e) => setCapInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCapability())}
                  placeholder="e.g., SSL scanning, Port analysis..."
                  className="bg-zinc-950 border-zinc-800 focus:ring-1 focus:ring-blue-500 rounded-sm h-10 text-zinc-50 placeholder:text-zinc-600"
                  maxLength={30}
                  data-testid="capability-input"
                />
                <Button aria-label="Add capability" onClick={addCapability} disabled={!capInput.trim() || capabilities.length >= 8} className="bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded-sm h-10 px-4" data-testid="add-capability-btn">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {capabilities.map((cap) => (
                  <span key={cap} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600/10 text-blue-400 border border-blue-600/20 rounded-sm text-xs font-semibold">
                    {cap}
                    <button data-testid={`remove-capability-${capabilities.indexOf(cap)}`} aria-label={`Remove ${cap}`} onClick={() => removeCapability(cap)} className="hover:text-red-400 transition-colors"><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
              {capabilities.length < 2 && <p className="text-xs text-amber-400">Add at least 2 capabilities to continue.</p>}
              <p className="text-[10px] text-zinc-600">{capabilities.length}/8 capabilities added</p>
              <RuntimeConfigurator engine={engine} setEngine={setEngine} terms={terms} setTerms={setTerms}/>
            </div>
          )}

          {/* Step 3: Pricing */}
          {step === 3 && (
            <div className="space-y-6" data-testid="step-pricing">
              <div>
                <h3 className="text-lg font-bold tracking-tight mb-1" style={{ fontFamily: "var(--font-heading)" }}>Pricing Model</h3>
                <p data-testid="builder-billing-status" className="text-xs text-zinc-400">Executable agents are free during beta. Paid subscriptions and creator payouts are not active.</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {PRICING_OPTIONS.map((opt) => {
                  const active = pricingType === opt.id;
                  return (
                    <button
                      key={opt.id}
                      disabled={opt.id !== 'free'}
                      onClick={() => setPricingType(opt.id)}
                      className={`text-left p-4 rounded-sm border transition-all ${
                        active ? `bg-${opt.color}-600/10 border-${opt.color}-600/30` : "bg-zinc-800/50 border-zinc-800 hover:border-zinc-700"
                      }`}
                      data-testid={`pricing-select-${opt.id}`}
                    >
                      <span className={`text-sm font-semibold ${active ? `text-${opt.color}-400` : "text-zinc-300"}`}>{opt.label}</span>
                      <p className="text-xs text-zinc-500 mt-1">{opt.desc}</p>
                    </button>
                  );
                })}
              </div>
              {pricingType !== "free" && (
                <div>
                  <Label className="text-xs tracking-[0.15em] uppercase text-zinc-400 font-bold">Monthly Price (USD)</Label>
                  <div className="relative mt-2">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 text-sm">$</span>
                    <Input
                      type="number"
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                      placeholder="9.00"
                      min="1"
                      max="999"
                      step="0.01"
                      className="bg-zinc-950 border-zinc-800 focus:ring-1 focus:ring-blue-500 rounded-sm h-11 text-zinc-50 placeholder:text-zinc-600 pl-8"
                      data-testid="agent-price-input"
                    />
                  </div>
                  <p className="text-[10px] text-zinc-500 mt-2">You earn <span className="text-emerald-400 font-semibold">80%</span> ({price ? `$${(parseFloat(price) * 0.8).toFixed(2)}` : "$0.00"}) per subscriber per month.</p>
                </div>
              )}
            </div>
          )}

          {/* Step 4: Review */}
          {step === 4 && (
            <div className="space-y-6" data-testid="step-review">
              <div>
                <h3 className="text-lg font-bold tracking-tight mb-1" style={{ fontFamily: "var(--font-heading)" }}>Review & Submit</h3>
                <p className="text-xs text-zinc-500">Publish your bounded scan configuration to the marketplace.</p>
                <p data-testid="builder-review-engine" className="text-sm text-cyan-300">Engine: {engine} · {terms.split('\n').filter(t => t.trim()).length} policy terms</p>
              </div>
              {/* Preview Card */}
              <div className="trace-border rounded-sm bg-zinc-800/30 p-5">
                <div className="flex items-center gap-2 mb-1 text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold">
                  <Eye className="w-3 h-3" /> Marketplace Preview
                </div>
                <div className="trace-border rounded-sm bg-zinc-900 p-5 mt-3">
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-10 h-10 rounded-sm bg-zinc-800 flex items-center justify-center">
                      <CatIcon className="w-5 h-5 text-blue-500" strokeWidth={1.5} />
                    </div>
                    <span className={`text-[9px] tracking-[0.2em] uppercase font-bold px-2 py-0.5 rounded-sm ${
                      pricingType === "free" ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20" :
                      pricingType === "freemium" ? "bg-blue-500/10 text-blue-400 border border-blue-500/20" :
                      "bg-amber-500/10 text-amber-400 border border-amber-500/20"
                    }`}>{pricingType.toUpperCase()}</span>
                  </div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-sm font-semibold tracking-tight text-zinc-50" style={{ fontFamily: "var(--font-heading)" }}>{name || "Agent Name"}</h3>
                    <span className="text-[8px] px-1.5 py-0.5 bg-zinc-700/50 text-zinc-400 border border-zinc-700 rounded-sm font-bold uppercase tracking-wider">Community</span>
                  </div>
                  <p className="text-xs text-zinc-400 leading-relaxed mb-3">{description || "Agent description..."}</p>
                  <div className="flex gap-1 flex-wrap mb-3">
                    {capabilities.map((cap) => (
                      <span key={cap} className="text-[9px] px-1.5 py-0.5 bg-zinc-800 text-zinc-500 rounded-sm">{cap}</span>
                    ))}
                  </div>
                  <div className="flex items-center justify-between pt-3 border-t border-zinc-800">
                    <span className="text-xs text-zinc-500 capitalize">{category?.replace("_", " ") || "Category"}</span>
                    {pricingType !== "free" && price ? (
                      <span className="text-sm font-bold text-zinc-200" style={{ fontFamily: "var(--font-heading)" }}>${price}<span className="text-xs text-zinc-500 font-normal">/mo</span></span>
                    ) : (
                      <span className="text-xs text-emerald-400 font-semibold">Free</span>
                    )}
                  </div>
                </div>
              </div>
              {/* Details Summary */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-zinc-800/50 rounded-sm p-3 border border-zinc-800">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-1">Category</div>
                  <div className="text-zinc-300 capitalize">{category?.replace("_", " ") || "—"}</div>
                </div>
                <div className="bg-zinc-800/50 rounded-sm p-3 border border-zinc-800">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-1">Revenue Share</div>
                  <div className="text-zinc-300 font-semibold">Not active in beta</div>
                </div>
                <div className="bg-zinc-800/50 rounded-sm p-3 border border-zinc-800">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-1">Capabilities</div>
                  <div className="text-zinc-300">{capabilities.length} defined</div>
                </div>
                <div className="bg-zinc-800/50 rounded-sm p-3 border border-zinc-800">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-1">Availability</div>
                  <div className="text-zinc-300">After publishing</div>
                </div>
              </div>
              {error && <p className="text-sm text-red-400" data-testid="submit-error">{error}</p>}
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-zinc-800">
            <Button
              onClick={() => step > 0 ? setStep(step - 1) : navigate("/marketplace")}
              variant="outline"
              className="border-zinc-800 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-50 rounded-sm bg-transparent"
              data-testid="builder-back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> {step > 0 ? "Back" : "Cancel"}
            </Button>
            {step < STEPS.length - 1 ? (
              <Button
                onClick={() => setStep(step + 1)}
                disabled={!canProceed()}
                className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm font-semibold"
                data-testid="builder-next-btn"
              >
                Continue <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm font-semibold"
                data-testid="builder-submit-btn"
              >
                {submitting ? "Publishing..." : "Publish Executable Agent"} {!submitting && <ArrowRight className="w-4 h-4 ml-1" />}
              </Button>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
